/**
 * The per-tenant image/media slots used by the customer app, mapped to their default filenames
 * inside packages/tenants/assets/customer/<tenant>/.
 *
 * Only filenames actually referenced by customer/src are slotted. The asset folders also carry
 * files that are dead in customer (onboarding-prop.png, pl-cloud.svg, pl-crates.svg,
 * TIACA_logo.png, Fragile/Stackable/TopLoad/ReqOrientation.svg) - slotting those would invent
 * configuration nobody reads.
 *
 * The default for every slot stays "commit a file into the tenant's folder". The theme's `assets`
 * override re-points one slot without a deploy, and its value is a URL: staff upload a file to the
 * `customerThemeAssets` bucket and the override holds the public URL that upload returns.
 */
export const CUSTOMER_ASSET_SLOTS = {
  logo: 'logo.svg',
  logoFooter: 'logo-footer.svg',
  favicon: 'favicon.svg',
  plane: 'pelicargo-plane.svg',
  supportIcon: 'support-icon.svg',
  signatureLabel: 'signature-label.svg',
  authPanelVideo: 'demo.mp4',
  authPanelPoster: 'demothumbnail.png',
} as const satisfies Record<string, string>;

export type CustomerAssetSlot = keyof typeof CUSTOMER_ASSET_SLOTS;

export const CUSTOMER_ASSET_SLOT_KEYS = Object.keys(CUSTOMER_ASSET_SLOTS) as CustomerAssetSlot[];

export type CustomerAssetOverrides = Partial<Record<CustomerAssetSlot, string>>;

/**
 * The extension, lowercased and stripped to alphanumerics, or `''` when there is none.
 *
 * The stripping is load-bearing rather than cosmetic: an uploaded filename is user input and this
 * feeds the storage object path, so `logo.svg/../../x` must not survive as path syntax.
 */
const extensionOf = (filename: string) =>
  filename.includes('.') ? (filename.split('.').pop() ?? '').toLowerCase().replace(/[^a-z0-9]/g, '') : '';

export type CustomerAssetMediaKind = 'image' | 'video';

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm']);

/**
 * Whether a slot holds a still image or a video, derived from its default filename so there is one
 * list to maintain instead of two that can disagree. Drives the upload control's `accept` filter
 * and whether the preview renders an `<img>` or a `<video>`.
 */
export const customerAssetMediaKind = (slot: CustomerAssetSlot): CustomerAssetMediaKind =>
  VIDEO_EXTENSIONS.has(extensionOf(CUSTOMER_ASSET_SLOTS[slot])) ? 'video' : 'image';

/**
 * Accepted upload MIME types per media kind. Also what the bucket is created with
 * (`allowedMimeTypes` in api/src/initializations/supabase.ts), so a file the control accepts is a
 * file storage accepts - the two cannot drift into rejecting each other.
 */
// `image/vnd.microsoft.icon` and `image/x-icon` are both here because browsers disagree about
// which one they report for the same `.ico` file, and a favicon is one of these slots - listing
// only one of them rejects half of all `.ico` uploads depending on the browser.
export const CUSTOMER_ASSET_MIME_TYPES: Record<CustomerAssetMediaKind, string[]> = {
  image: [
    'image/svg+xml',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/avif',
    'image/gif',
    'image/x-icon',
    'image/vnd.microsoft.icon',
  ],
  video: ['video/mp4', 'video/webm'],
};

/** Matches the documents flow's client-side cap. These are logos and one short loop. */
export const CUSTOMER_ASSET_MAX_BYTES = 5 * 1024 * 1024;

/**
 * The longest base64 string `CUSTOMER_ASSET_MAX_BYTES` can encode to, so an oversized upload can be
 * refused by input validation instead of being decoded first.
 *
 * Worth the arithmetic: bytes reach the API as base64 in the request body, and checking only the
 * decoded length means `Buffer.from` has already materialised the whole thing - so an accidental
 * 40MB drop is fully buffered in memory before anything rejects it.
 *
 * base64 emits 4 characters per 3 bytes and pads to a multiple of 4, which is exactly what
 * `ceil(bytes / 3) * 4` gives.
 */
export const CUSTOMER_ASSET_MAX_BASE64_LENGTH = Math.ceil(CUSTOMER_ASSET_MAX_BYTES / 3) * 4;

/**
 * The MIME types a slot accepts. Returns the list rather than a yes/no, because every caller needs
 * the list anyway - to build the file picker's filter, or to say what *would* have been accepted
 * when it refuses something.
 */
export const customerAssetMimeTypes = (slot: CustomerAssetSlot) =>
  CUSTOMER_ASSET_MIME_TYPES[customerAssetMediaKind(slot)];

/** Every object ever uploaded for a slot shares this prefix, so the stale ones can be swept. */
export const customerAssetObjectPrefix = (slot: CustomerAssetSlot) => `${slot}-`;

/**
 * Where a slot's uploaded object lives inside the `customerThemeAssets` bucket, keyed by tenant so
 * one tenant's upload can never land on another's object.
 *
 * The content digest in the name is not decoration. The stored override is a permanent public URL,
 * so replacing a slot by re-writing a fixed `<tenant>/<slot>.<ext>` path would leave every browser
 * that already fetched it - and anything caching in front of storage - serving the old bytes with
 * no way to bust it. Keying the name by content means new bytes always mean a new URL.
 */
export const customerAssetObjectPath = (
  tenantId: string,
  slot: CustomerAssetSlot,
  { filename, digest }: { filename: string; digest: string },
) => {
  // An upload with no usable extension falls back to the slot default's, so the object still
  // carries one and storage/browsers can infer a type from the URL.
  const extension = extensionOf(filename) || extensionOf(CUSTOMER_ASSET_SLOTS[slot]);

  return `${tenantId}/${customerAssetObjectPrefix(slot)}${digest}.${extension}`;
};

/**
 * The reserved `assets` value meaning "render nothing in this slot".
 *
 * Needed because an *absent* override already means something else - "use the file the tenant
 * ships" - so clearing the field cannot express "no image here". A tenant with no footer logo had
 * no way to say so.
 *
 * A bare word rather than a URL-ish sentinel because it is typed and read by staff in the theme
 * editor. It cannot collide with a real override: every override the editor writes is a URL, and
 * `resolveCustomerAsset` checks for this before anything else.
 */
export const CUSTOMER_ASSET_NONE = 'none';

/**
 * An override that addresses a location rather than a file inside the tenant's asset folder.
 *
 * Every override written by the theme editor is a URL now - an uploaded asset's public URL, or one
 * pasted into the field by hand - so this is true for all of them. It stays a test rather than an
 * assumption because the schema deliberately does not enforce it: see `resolveCustomerAsset`.
 */
export const isExternalAsset = (value: string) => /^(https?:)?\/\//.test(value) || value.startsWith('/');

/**
 * Resolves a slot to the filename that should be handed to `getAsset()`, to a URL to use verbatim,
 * or to nothing at all. Returns a discriminated result rather than calling `getAsset` itself,
 * because `getAsset` reads `window.location` and so cannot live in a shared package.
 *
 * `none` is a real outcome, not an error: callers must render no element for it. Falling back to
 * the shipped default there would make "remove this image" impossible.
 *
 * The `tenant-file` branch for a *non-empty override* is a compatibility path, not the live one:
 * overrides used to be filenames inside the tenant's folder. `assets` is still typed as a
 * permissive string in the schema on purpose - the comment on `customerThemeConfigValidator`
 * explains why a hard failure there would blank a tenant's whole theme - so a row written before
 * uploads existed has to keep resolving instead of 404ing.
 */
export const resolveCustomerAsset = (
  slot: CustomerAssetSlot,
  override?: string,
): { kind: 'none' } | { kind: 'tenant-file'; filename: string } | { kind: 'external'; url: string } => {
  const override_ = override?.trim();

  // Checked before the fallback, which is the whole point: `none` has to beat the shipped default
  // rather than be treated as a filename to look for in the tenant's folder.
  if (override_ === CUSTOMER_ASSET_NONE) return { kind: 'none' };

  const value = override_ || CUSTOMER_ASSET_SLOTS[slot];

  return isExternalAsset(value) ? { kind: 'external', url: value } : { kind: 'tenant-file', filename: value };
};
